import { CheckOutlined, DeleteOutlined } from "@ant-design/icons"
import { Button, Tooltip, Typography } from "antd"
import { forwardRef } from "react"

import AnimateInFlex, { ANIMATION_BY_KEY } from "#blocks/AnimateInFlex"
import Flex from "#blocks/Flex"
import { ROOM_COLORS } from "#constants/classOptions"
import {
  HELP_REQUEST_PRIORITY,
  HelpRequestConstructor,
} from "#customTypes/composedTypes/helpRequestTypes"
import { useGetOneUser } from "#hooks/newHooks/useGetUser"

import { getRoomLabel } from "./helpRequestConstants"
import {
  AcknowledgerList,
  FuzzyTimestamp,
  UrgentTag,
} from "./HelpRequestShared"
import { useCanResolveHelpRequest } from "./useHelpRequests"

const { Text } = Typography

const HelpRequestItem = forwardRef<
  HTMLDivElement,
  {
    request: HelpRequestConstructor
    effectiveUserId: string
    onAcknowledge: (id: string, currentlyAcked: boolean) => void
    onResolve: (id: string) => void
  }
>(({ request, effectiveUserId, onAcknowledge, onResolve }, ref) => {
  const creator = useGetOneUser(request.createdBy)
  const isUrgent = request.priority === HELP_REQUEST_PRIORITY.URGENT
  const ackIds = request.acknowledgedByIds ?? []
  const alreadyAcked = ackIds.includes(effectiveUserId)
  const isCreator = effectiveUserId === request.createdBy
  const canResolve = useCanResolveHelpRequest(request.createdBy)
  const roomColor = ROOM_COLORS[request.room]

  return (
    <AnimateInFlex
      ref={ref}
      animation={ANIMATION_BY_KEY.leftSm}
      col
      style={{
        padding: "8px 10px",
        background: roomColor,
        borderRadius: 6,
        color: "white",
        marginBottom: 6,
        position: "relative",
      }}
    >
      <Flex
        style={{
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Flex style={{ alignItems: "baseline" }} gap={6}>
          <Text strong style={{ color: "white", fontSize: 13 }}>
            {creator?.name ?? "..."}
          </Text>
          <Text
            style={{
              color: "rgba(255,255,255,0.8)",
              fontSize: 11,
              marginLeft: 6,
            }}
          >
            {getRoomLabel(request.room)}
          </Text>
          {isUrgent && <UrgentTag size="sm" />}
        </Flex>
        <Flex gap={4}>
          {!isCreator && (
            <Tooltip title={alreadyAcked ? "Undo acknowledge" : "On My Way"}>
              <Button
                size="small"
                shape="circle"
                type="text"
                color="green"
                style={
                  alreadyAcked
                    ? {
                        background: "#52c41a",
                        borderColor: "#52c41a",
                        color: "white",
                      }
                    : {
                        color: "#52c41a",
                      }
                }
                icon={<CheckOutlined />}
                onClick={() => onAcknowledge(request.id, alreadyAcked)}
              />
            </Tooltip>
          )}
          {canResolve && (
            <Tooltip title="Resolve">
              <Button
                size="small"
                shape="circle"
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={() => onResolve(request.id)}
              />
            </Tooltip>
          )}
        </Flex>
      </Flex>

      <FuzzyTimestamp createdAt={request.createdAt} size="sm" />
      {request.comment && (
        <div
          style={{
            marginTop: 4,
            padding: "3px 6px",
            background: "rgba(255,255,255,0.15)",
            borderRadius: 4,
            fontSize: 11,
          }}
        >
          &ldquo;{request.comment}&rdquo;
        </div>
      )}
      <AcknowledgerList ids={ackIds} size="sm" />
    </AnimateInFlex>
  )
})

HelpRequestItem.displayName = "HelpRequestItem"

export default HelpRequestItem
